import { useAppTheme } from "@hooks/useAppTheme";
import type { FlashcardEntry } from "@memorang/types/src/session/new-session";
import { Box, MarkdownRenderer } from "@memorang/ui";
import { ImageBackground } from "expo-image";
import { useState } from "react";
import { Pressable, ScrollView, View, useWindowDimensions } from "react-native";
import type { ViewStyle } from "react-native";
import { Caption, Icon, Text } from "react-native-paper";
import Animated, {
	interpolate,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from "react-native-reanimated";
import { getPrincipleIllustration } from "../../../../../../../assets/praxis-proethica/illustrations/principles";

interface Props {
	cards: FlashcardEntry[];
	selectedChoiceIds?: string[];
}

type ReviewImageSource = string | number | { uri: string };

type CardFrontWithIllustration = FlashcardEntry["front"] & {
	illustration?: ReviewImageSource;
	illustrationImage?: ReviewImageSource;
	illustrationUrl?: ReviewImageSource;
};

const AT_RISK_PREFIX = "**At Immediate Risk";
const isAtRisk = (back: string) => back.trimStart().startsWith(AT_RISK_PREFIX);
const stripRiskPrefix = (back: string) =>
	back.replace(/^\*\*At Immediate Risk[^*]*\*\*:?\s*/i, "").trim();

const getIllustrationImage = (
	front: FlashcardEntry["front"],
): ReviewImageSource | undefined => {
	const extendedFront = front as CardFrontWithIllustration;
	return (
		extendedFront.illustration ??
		extendedFront.illustrationImage ??
		extendedFront.illustrationUrl ??
		getPrincipleIllustration(front.text)
	);
};

const toImageSource = (source?: ReviewImageSource) =>
	typeof source === "string" ? { uri: source } : source;

const StatusChip = ({
	color,
	bg,
	label,
}: {
	color: string;
	bg: string;
	label: string;
}) => (
	<Box
		flexDirection="row"
		alignItems="center"
		justifyContent="center"
		gap={4}
		paddingHorizontal={8}
		paddingVertical={4}
		minHeight={26}
		borderRadius={0}
		backgroundColor={bg}
	>
		<Caption
			style={{ color, fontWeight: "700", letterSpacing: 0.4, fontSize: 10 }}
		>
			{label}
		</Caption>
	</Box>
);

type AnswerStatus = "correct" | "incorrect" | "notPicked";

const getAnswerStatus = (selected: boolean, atRisk: boolean): AnswerStatus => {
	if (!selected) {
		return "notPicked";
	}
	return atRisk ? "correct" : "incorrect";
};

const AnswerStatusChip = ({ status }: { status: AnswerStatus }) => {
	const theme = useAppTheme();
	const config = {
		correct: {
			icon: "check-circle",
			label: "Selected correctly",
			color: theme.palette.success.main,
			bg: `${theme.palette.success.main}18`,
		},
		incorrect: {
			icon: "close-circle",
			label: "Selected incorrectly",
			color: theme.colors.error,
			bg: theme.colors.errorContainer,
		},
		notPicked: {
			icon: "minus-circle-outline",
			label: "Not selected",
			color: theme.colors.onSurfaceVariant,
			bg: theme.colors.surfaceVariant,
		},
	}[status];

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			justifyContent="center"
			gap={5}
			paddingHorizontal={8}
			paddingVertical={4}
			minHeight={26}
			backgroundColor={config.bg}
		>
			<Icon source={config.icon} size={14} color={config.color} />
			<Caption
				style={{
					color: config.color,
					fontWeight: "800",
					letterSpacing: 0.4,
					fontSize: 10,
				}}
			>
				{config.label}
			</Caption>
		</Box>
	);
};

const GridFlipCard = ({
	card,
	selected,
	style,
}: {
	card: FlashcardEntry;
	selected: boolean;
	style?: ViewStyle;
}) => {
	const theme = useAppTheme();
	const flipped = useSharedValue(0);
	const [isFlipped, setIsFlipped] = useState(false);
	const displayImage = getIllustrationImage(card.front) ?? card.front.image;

	const frontStyle = useAnimatedStyle(() => ({
		transform: [
			{ perspective: 1000 },
			{ rotateY: `${interpolate(flipped.value, [0, 1], [0, 180])}deg` },
		],
		opacity: flipped.value < 0.5 ? 1 : 0,
	}));

	const backStyle = useAnimatedStyle(() => ({
		transform: [
			{ perspective: 1000 },
			{ rotateY: `${interpolate(flipped.value, [0, 1], [180, 360])}deg` },
		],
		opacity: flipped.value >= 0.5 ? 1 : 0,
	}));

	const handleFlip = () => {
		const next = flipped.value === 0 ? 1 : 0;
		flipped.value = withTiming(next, { duration: 350 });
		setIsFlipped(next === 1);
	};

	const baseFaceStyle = {
		position: "absolute" as const,
		width: "100%" as const,
		height: "100%" as const,
		backfaceVisibility: "hidden" as const,
		borderRadius: 0,
		borderWidth: 1,
		borderColor: theme.colors.outlineVariant,
		backgroundColor: theme.colors.surface,
		overflow: "hidden" as const,
	};

	const atRisk = isAtRisk(card.back);
	const bodyText = atRisk ? stripRiskPrefix(card.back) : card.back;
	const answerStatus = getAnswerStatus(selected, atRisk);

	return (
		<View style={[{ flex: 1, aspectRatio: 1 }, style]}>
			<Animated.View
				style={[baseFaceStyle, frontStyle]}
				accessibilityElementsHidden={isFlipped}
				importantForAccessibility={isFlipped ? "no-hide-descendants" : "auto"}
			>
				<Pressable
					onPress={handleFlip}
					style={{ flex: 1 }}
					accessibilityRole="button"
					accessibilityLabel={card.front.text}
					accessibilityHint="Double tap to flip"
				>
					{displayImage ? (
						<>
							<ImageBackground
								source={toImageSource(displayImage)}
								contentFit="contain"
								style={{
									flex: 1,
									backgroundColor: "#FAF4ED",
								}}
							>
								<Box
									flexDirection="row"
									justifyContent="space-between"
									alignItems="flex-start"
									padding={12}
								>
									<AnswerStatusChip status={answerStatus} />
									<Box
										width={36}
										height={36}
										alignItems="center"
										justifyContent="center"
										backgroundColor={theme.colors.primary}
									>
										<Icon
											source="rotate-3d-variant"
											size={20}
											color={theme.colors.onPrimary}
										/>
									</Box>
								</Box>
							</ImageBackground>
							<Box padding={12} backgroundColor={theme.colors.primary}>
								<Text
									variant="titleSmall"
									numberOfLines={2}
									style={{
										color: theme.colors.white,
									}}
								>
									{card.front.text}
								</Text>
								<Box
									flexDirection="row"
									alignItems="center"
									gap={6}
									paddingTop={6}
								>
									<Icon
										source="gesture-tap"
										size={16}
										color={theme.colors.white}
									/>
									<Caption style={{ color: theme.colors.white }}>Flip</Caption>
								</Box>
							</Box>
						</>
					) : (
						<Box flex={1} justifyContent="center" gap={12} padding={16}>
							<Icon
								source="image-off-outline"
								size={32}
								color={theme.colors.onSurfaceVariant}
							/>
							<Text
								variant="titleMedium"
								style={{ color: theme.colors.onSurface, fontWeight: "700" }}
							>
								{card.front.text}
							</Text>
							<Caption>Flip</Caption>
						</Box>
					)}
				</Pressable>
			</Animated.View>

			<Animated.View
				style={[
					baseFaceStyle,
					backStyle,
					{ pointerEvents: isFlipped ? "auto" : "none" },
				]}
				accessibilityElementsHidden={!isFlipped}
				importantForAccessibility={isFlipped ? "auto" : "no-hide-descendants"}
			>
				{atRisk ? (
					<>
						<Box
							flexDirection="row"
							justifyContent="space-between"
							alignItems="center"
							paddingHorizontal={12}
							padding={12}
							borderWidth={1}
							borderColor={theme.colors.outlineVariant}
							style={{
								borderTopWidth: 0,
								borderLeftWidth: 0,
								borderRightWidth: 0,
							}}
						>
							<Box
								flexDirection="row"
								gap={6}
								flexWrap="wrap"
								alignItems="center"
							>
								<StatusChip
									color={theme.colors.error}
									bg={theme.colors.errorContainer}
									label="At immediate risk"
								/>
								<AnswerStatusChip status={answerStatus} />
							</Box>
							<Pressable
								onPress={handleFlip}
								accessibilityRole="button"
								accessibilityLabel="Flip card back"
								style={{
									width: 36,
									height: 36,
									alignItems: "center",
									justifyContent: "center",
									backgroundColor: theme.colors.primary,
								}}
							>
								<Icon
									source="rotate-3d-variant"
									size={20}
									color={theme.colors.onPrimary}
								/>
							</Pressable>
						</Box>
						<ScrollView
							style={{ flex: 1 }}
							contentContainerStyle={{ padding: 16 }}
							showsVerticalScrollIndicator={false}
						>
							<MarkdownRenderer
								text={bodyText}
								variant="explanation"
								fontSize={15}
								customStyles={{
									heading4: { fontSize: 15, lineHeight: 22.5, marginBottom: 6 },
									paragraph: { fontSize: 15, lineHeight: 22.5 },
									text: { fontSize: 15, lineHeight: 22.5 },
								}}
							/>
						</ScrollView>
					</>
				) : (
					<>
						<Box
							flexDirection="row"
							justifyContent="space-between"
							alignItems="center"
							padding={12}
							borderWidth={1}
							borderColor={theme.colors.outlineVariant}
							style={{
								borderTopWidth: 0,
								borderLeftWidth: 0,
								borderRightWidth: 0,
							}}
						>
							<Box
								flexDirection="row"
								gap={6}
								flexWrap="wrap"
								alignItems="center"
							>
								<StatusChip
									color={theme.palette.success.main}
									bg={`${theme.palette.success.main}18`}
									label="No immediate risk"
								/>
								<AnswerStatusChip status={answerStatus} />
							</Box>
							<Pressable
								onPress={handleFlip}
								accessibilityRole="button"
								accessibilityLabel="Flip card back"
								style={{
									width: 36,
									height: 36,
									alignItems: "center",
									justifyContent: "center",
									backgroundColor: `${theme.palette.success.main}18`,
								}}
							>
								<Icon
									source="rotate-3d-variant"
									size={20}
									color={theme.palette.success.main}
								/>
							</Pressable>
						</Box>
						<Box flex={1} gap={8} padding={16} alignItems="flex-start">
							<Box
								width={40}
								height={40}
								borderRadius={0}
								backgroundColor={`${theme.palette.success.main}18`}
								alignItems="center"
								justifyContent="center"
							>
								<Icon
									source="check"
									size={22}
									color={theme.palette.success.main}
								/>
							</Box>
							<Text
								variant="titleMedium"
								style={{
									color: theme.palette.success.main,
									fontWeight: "700",
								}}
							>
								No Immediate Risk
							</Text>
						</Box>
					</>
				)}
			</Animated.View>
		</View>
	);
};

const ReviewFlipGrid = ({ cards, selectedChoiceIds = [] }: Props) => {
	const { width } = useWindowDimensions();
	const isSingleColumn = width < 760;
	const rowSize = isSingleColumn ? 1 : 2;
	const rows = cards.reduce<FlashcardEntry[][]>((acc, card, index) => {
		if (index % rowSize === 0) {
			acc.push([]);
		}
		acc[acc.length - 1]?.push(card);
		return acc;
	}, []);

	return (
		<Box width="100%" gap={16}>
			{rows.map((row) => (
				<Box
					key={row.map((card) => card.front.text).join("|")}
					width="100%"
					flexDirection={isSingleColumn ? "column" : "row"}
					gap={16}
				>
					{row.map((card) => {
						const cardIndex = cards.indexOf(card);
						return (
							<GridFlipCard
								key={card.front.text}
								card={card}
								selected={selectedChoiceIds.includes(
									String.fromCharCode(65 + cardIndex),
								)}
							/>
						);
					})}
					{!isSingleColumn && row.length === 1 ? (
						<View style={{ flex: 1 }} />
					) : null}
				</Box>
			))}
		</Box>
	);
};

export default ReviewFlipGrid;
